// ── Evolution Dashboard Tab ──────────────────────────────────
// Shows the self-evolution system status: pattern analysis,
// A/B test results, evolution log, living tree controls,
// and a "Run Evolution Now" trigger.

import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../api';

const API_URL = process.env.REACT_APP_API_URL || '';

const MODES = ['idea', 'code', 'resume', 'decide', 'write', 'plan', 'learn'];

export default function EvolutionTab() {
  const [mode, setMode] = useState('idea');
  const [modeAnalysis, setModeAnalysis] = useState(null);
  const [patternDetail, setPatternDetail] = useState(null);
  const [evolutionLog, setEvolutionLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evolving, setEvolving] = useState(false);
  const [evolveResult, setEvolveResult] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  // ── Load mode analysis ──────────────────────────────────────
  const loadModeAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [modeRes, logRes] = await Promise.all([
        authFetch(`${API_URL}/api/patterns/analysis/mode/${mode}`),
        authFetch(`${API_URL}/api/patterns/evolution-log?limit=20`),
      ]);
      const modeData = await modeRes.json();
      const logData = await logRes.json();
      setModeAnalysis(modeData);
      setEvolutionLog(Array.isArray(logData) ? logData : []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [mode]);

  useEffect(() => { loadModeAnalysis(); }, [loadModeAnalysis]);

  // ── Load pattern detail ─────────────────────────────────────
  const loadPatternDetail = async (patternId) => {
    try {
      const [analysisRes, abRes] = await Promise.all([
        authFetch(`${API_URL}/api/patterns/${patternId}/analysis?mode=${mode}`),
        authFetch(`${API_URL}/api/patterns/${patternId}/ab-results`).catch(() => null),
      ]);
      const analysis = await analysisRes.json();
      let abResults = null;
      if (abRes && abRes.ok) abResults = await abRes.json();
      setPatternDetail({ patternId, analysis, abResults });
    } catch {}
  };

  // ── Trigger evolution ───────────────────────────────────────
  const handleEvolve = async () => {
    setEvolving(true);
    setEvolveResult(null);
    try {
      const res = await authFetch(`${API_URL}/api/patterns/evolve-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      setEvolveResult(data);
      loadModeAnalysis(); // refresh
    } catch (err) {
      setEvolveResult({ error: err.message });
    }
    setEvolving(false);
  };

  // ── Trigger evolution for a specific pattern ────────────────
  const handleEvolvePattern = async (patternId) => {
    setEvolving(true);
    setEvolveResult(null);
    try {
      const res = await authFetch(`${API_URL}/api/patterns/${patternId}/evolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      setEvolveResult(data);
      loadPatternDetail(patternId);
      loadModeAnalysis();
    } catch (err) {
      setEvolveResult({ error: err.message });
    }
    setEvolving(false);
  };

  // ── Morphogenesis scan ──────────────────────────────────────
  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await authFetch(`${API_URL}/api/patterns/living-tree/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes: [], sessionMeta: {} }),
      });
      const data = await res.json();
      setScanResult(data);
    } catch (err) {
      setScanResult({ error: err.message });
    }
    setScanning(false);
  };

  // ── Render helpers ──────────────────────────────────────────

  const renderBar = (value, max, color) => {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
      <div className="evo-bar">
        <div className="evo-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    );
  };

  const trendIcon = (dir) => {
    if (dir === 'improving') return '↑';
    if (dir === 'degrading') return '↓';
    return '→';
  };

  const trendColor = (dir) => {
    if (dir === 'improving') return '#4ade80';
    if (dir === 'degrading') return '#f87171';
    return '#94a3b8';
  };

  const outcomeColor = (outcome) => {
    if (outcome === 'promoted') return '#4ade80';
    if (outcome === 'reverted' || outcome === 'early_revert') return '#f87171';
    if (outcome === 'testing' || outcome === 'pending') return '#fbbf24';
    return '#94a3b8';
  };

  // ── Main render ─────────────────────────────────────────────

  return (
    <div className="evo-tab">
      {/* Header */}
      <div className="evo-header">
        <div>
          <h2 className="evo-title">Pattern Evolution</h2>
          <p className="evo-subtitle">The system learns which thinking strategies work best and improves them autonomously.</p>
        </div>
        <button
          className="evo-evolve-btn"
          onClick={handleEvolve}
          disabled={evolving}
        >
          {evolving ? '◌ Evolving...' : '⟳ Evolve Now'}
        </button>
      </div>

      {/* Mode selector */}
      <div className="evo-mode-bar">
        {MODES.map(m => (
          <button
            key={m}
            className={`evo-mode-btn ${mode === m ? 'evo-mode-btn--active' : ''}`}
            onClick={() => { setMode(m); setPatternDetail(null); }}
          >
            {m.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Evolution result banner */}
      {evolveResult && (
        <div className={`evo-banner ${evolveResult.evolved ? 'evo-banner--success' : evolveResult.error ? 'evo-banner--error' : 'evo-banner--info'}`}>
          {evolveResult.evolved ? (
            <>
              Evolved <strong>{evolveResult.patternId}</strong> from v{evolveResult.fromVersion} to v{evolveResult.toVersion}.
              {' '}Mutations: {(evolveResult.mutations || []).map(m => m.description).join('; ')}.
              {' '}A/B test started.
            </>
          ) : (
            evolveResult.error || evolveResult.reason || 'No evolution performed.'
          )}
          <button className="evo-banner-close" onClick={() => setEvolveResult(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="evo-loading">Analyzing patterns...</div>
      ) : error ? (
        <div className="evo-error">{error}</div>
      ) : (
        <>
          {/* Pattern Rankings */}
          <div className="evo-section">
            <h3 className="evo-section-title">Pattern Rankings — {mode} mode</h3>
            {modeAnalysis?.patternRankings?.length > 0 ? (
              <div className="evo-rankings">
                <div className="evo-rankings-header">
                  <span>Pattern</span>
                  <span>Avg Score Delta</span>
                  <span>Tokens/Run</span>
                  <span>Efficiency</span>
                  <span>Runs</span>
                  <span></span>
                </div>
                {modeAnalysis.patternRankings.map((r, i) => (
                  <div
                    key={r.patternId}
                    className={`evo-ranking-row ${i === 0 ? 'evo-ranking-row--best' : ''} ${patternDetail?.patternId === r.patternId ? 'evo-ranking-row--selected' : ''}`}
                    onClick={() => loadPatternDetail(r.patternId)}
                  >
                    <span className="evo-rank-name">
                      {i === 0 && <span className="evo-rank-badge">BEST</span>}
                      {r.patternId}
                    </span>
                    <span className="evo-rank-delta" style={{ color: r.avgScoreDelta > 0 ? '#4ade80' : '#f87171' }}>
                      {r.avgScoreDelta > 0 ? '+' : ''}{r.avgScoreDelta}
                    </span>
                    <span className="evo-rank-tokens">{r.avgTokensUsed.toLocaleString()}</span>
                    <span className="evo-rank-efficiency">
                      {renderBar(r.deltaPerKToken, 0.01, '#818cf8')}
                      <span>{r.deltaPerKToken}</span>
                    </span>
                    <span className="evo-rank-runs">{r.sampleSize}</span>
                    <button
                      className="evo-rank-evolve"
                      onClick={(e) => { e.stopPropagation(); handleEvolvePattern(r.patternId); }}
                      disabled={evolving}
                      title="Evolve this pattern"
                    >
                      ⟳
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="evo-empty">
                <p>No telemetry data yet for <strong>{mode}</strong> mode.</p>
                <p className="evo-empty-hint">Run some patterns (debate, refine, etc.) to start collecting data. The system needs 15+ executions to evolve.</p>
              </div>
            )}
          </div>

          {/* Pattern Detail */}
          {patternDetail && patternDetail.analysis && !patternDetail.analysis.error && (
            <div className="evo-section">
              <h3 className="evo-section-title">
                {patternDetail.patternId} — Deep Analysis
                <span className="evo-trend" style={{ color: trendColor(patternDetail.analysis.trendDirection) }}>
                  {trendIcon(patternDetail.analysis.trendDirection)} {patternDetail.analysis.trendDirection}
                </span>
              </h3>

              {/* Stage effectiveness */}
              <div className="evo-stages">
                <div className="evo-stages-header">
                  <span>Stage</span>
                  <span>Type</span>
                  <span>Avg Duration</span>
                  <span>Avg Tokens</span>
                  <span>Quality</span>
                  <span>Errors</span>
                </div>
                {Object.values(patternDetail.analysis.stageEffectiveness || {}).map(s => (
                  <div key={s.stageName} className={`evo-stage-row ${patternDetail.analysis.bottleneckStages?.some(b => b.stageName === s.stageName) ? 'evo-stage-row--bottleneck' : ''}`}>
                    <span className="evo-stage-name">
                      {s.stageName}
                      {patternDetail.analysis.bottleneckStages?.some(b => b.stageName === s.stageName) && (
                        <span className="evo-bottleneck-badge">BOTTLENECK</span>
                      )}
                    </span>
                    <span className="evo-stage-type">{s.stageType}</span>
                    <span>{s.avgDurationMs}ms</span>
                    <span>{s.avgTokens}</span>
                    <span style={{ color: s.avgQualityContribution > 0.3 ? '#4ade80' : s.avgQualityContribution > 0.1 ? '#fbbf24' : '#f87171' }}>
                      {s.avgQualityContribution}
                    </span>
                    <span style={{ color: s.errorRate > 0.1 ? '#f87171' : '#94a3b8' }}>
                      {(s.errorRate * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>

              {/* Bottleneck alerts */}
              {patternDetail.analysis.bottleneckStages?.length > 0 && (
                <div className="evo-bottlenecks">
                  {patternDetail.analysis.bottleneckStages.map((b, i) => (
                    <div key={i} className="evo-bottleneck-item">
                      <span className="evo-bottleneck-icon">!</span>
                      <span><strong>{b.stageName}</strong>: {b.reason}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Version breakdown */}
              {patternDetail.analysis.versionBreakdown?.length > 1 && (
                <div className="evo-versions">
                  <h4>Version Comparison</h4>
                  {patternDetail.analysis.versionBreakdown.map(v => (
                    <div key={v.version} className="evo-version-row">
                      <span className="evo-version-label">v{v.version}</span>
                      <span style={{ color: v.avgScoreDelta > 0 ? '#4ade80' : '#f87171' }}>
                        delta: {v.avgScoreDelta > 0 ? '+' : ''}{v.avgScoreDelta}
                      </span>
                      <span>{v.avgDurationMs}ms</span>
                      <span>{v.avgTokensUsed} tok</span>
                      <span>n={v.sampleSize}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* A/B Test Results */}
              {patternDetail.abResults && (
                <div className="evo-ab-test">
                  <h4>A/B Test — v{patternDetail.abResults.controlVersion} vs v{patternDetail.abResults.variantVersion}</h4>
                  <div className="evo-ab-row">
                    <div className="evo-ab-side">
                      <div className="evo-ab-label">Control (v{patternDetail.abResults.controlVersion})</div>
                      <div className="evo-ab-value">{patternDetail.abResults.controlAvgDelta}</div>
                      <div className="evo-ab-count">{patternDetail.abResults.sampleSize?.control || 0} runs</div>
                    </div>
                    <div className="evo-ab-vs">
                      <span className="evo-ab-improvement" style={{ color: patternDetail.abResults.improvement > 0 ? '#4ade80' : '#f87171' }}>
                        {patternDetail.abResults.improvement > 0 ? '+' : ''}{patternDetail.abResults.improvement}%
                      </span>
                      <span className="evo-ab-confidence">{patternDetail.abResults.confidence} confidence</span>
                    </div>
                    <div className="evo-ab-side">
                      <div className="evo-ab-label">Variant (v{patternDetail.abResults.variantVersion})</div>
                      <div className="evo-ab-value">{patternDetail.abResults.variantAvgDelta}</div>
                      <div className="evo-ab-count">{patternDetail.abResults.sampleSize?.variant || 0} runs</div>
                    </div>
                  </div>
                  <div className="evo-ab-status">
                    Status: <strong>{patternDetail.abResults.status}</strong>
                    {patternDetail.abResults.recommendation && <> — Recommendation: <strong>{patternDetail.abResults.recommendation}</strong></>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Morphogenesis Scanner */}
          <div className="evo-section">
            <h3 className="evo-section-title">
              Living Tree Scanner
              <button className="evo-scan-btn" onClick={handleScan} disabled={scanning}>
                {scanning ? '◌ Scanning...' : '◎ Run Scan'}
              </button>
            </h3>
            {scanResult && !scanResult.error && (
              <div className="evo-scan-results">
                {scanResult.bottlenecks?.length > 0 ? (
                  scanResult.bottlenecks.map((b, i) => (
                    <div key={i} className="evo-scan-item">
                      <span className={`evo-scan-severity evo-scan-severity--${b.severity}`}>
                        {b.severity}
                      </span>
                      <span className="evo-scan-type">{b.type.replace(/_/g, ' ')}</span>
                      <span className="evo-scan-remedy">
                        Remedy: {b.remedy?.pattern || b.remedy?.action || 'none'}
                      </span>
                      <span className="evo-scan-affected">
                        {b.affectedNodes?.length || 0} nodes affected
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="evo-scan-clean">No bottlenecks detected — tree is healthy.</div>
                )}
              </div>
            )}
            {scanResult?.error && <div className="evo-error">{scanResult.error}</div>}
            {!scanResult && <p className="evo-empty-hint">Scans your current session's tree for thinking bottlenecks (deadlocks, echo chambers, etc.)</p>}
          </div>

          {/* Evolution Log */}
          <div className="evo-section">
            <h3 className="evo-section-title">Evolution History</h3>
            {evolutionLog.length > 0 ? (
              <div className="evo-log">
                {evolutionLog.map((entry, i) => (
                  <div key={i} className="evo-log-entry">
                    <span className="evo-log-time">{new Date(entry.timestamp).toLocaleDateString()}</span>
                    <span className="evo-log-pattern">{entry.patternId}</span>
                    <span className="evo-log-versions">v{entry.fromVersion} → v{entry.toVersion}</span>
                    <span className="evo-log-outcome" style={{ color: outcomeColor(entry.outcome) }}>
                      {entry.outcome}
                    </span>
                    <span className="evo-log-mutations">
                      {(entry.mutations || []).map(m => m.description).join(', ') || '—'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="evo-empty-hint">No evolution history yet. Run "Evolve Now" or enable auto-evolution to start.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
